import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import Icon from '@/components/redesign/icons/Icons';
import CustomLinkRenderer from '../Markdown/CustomLinkRenderer';
import rehypeMathjax from 'rehype-mathjax';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

const ReactMarkdown = dynamic(() => import('react-markdown'));

// Concatenate the visible text of a hast node tree.
const getNodeText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  if (node.children) return node.children.map(getNodeText).join('');
  return '';
};

const renderInlineNode = (node, key) => {
  if (!node) return null;
  if (node.type === 'text') return node.value || '';

  const children = node.children?.map((child, index) =>
    renderInlineNode(child, index),
  );

  if (node.tagName === 'a') {
    return (
      <CustomLinkRenderer key={key} href={node.properties?.href}>
        {children}
      </CustomLinkRenderer>
    );
  }

  return <React.Fragment key={key}>{children}</React.Fragment>;
};

const renderReferenceTitle = (node) =>
  node.children?.map((child, index) => renderInlineNode(child, index)) ||
  getNodeText(node);

// A single source rendered as an evidence card (teal rail, serif title, excerpt).
function ReferenceCard({ title, excerpt }) {
  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-surface-paper-2 p-3 transition-colors hover:border-brand-200">
      <span className="absolute inset-y-0 left-0 w-[2.5px] bg-teal-500/0 transition-colors duration-200 group-hover:bg-teal-500" />
      <div className="flex items-start gap-1.5">
        <Icon.Book
          width={12}
          height={12}
          className="mt-[2px] flex-shrink-0 text-teal-700"
        />
        <span className="font-serif text-[12.5px] font-semibold leading-snug text-ink-900">
          {title}
        </span>
      </div>
      {excerpt && (
        <p className="mt-1.5 pl-[18px] text-[11.5px] leading-[1.55] text-ink-600">
          {excerpt}
        </p>
      )}
    </div>
  );
}

// Markdown overrides that turn the reference list into evidence cards.
// References arrive as: <ul><li><em>Source</em><ul><li>excerpt</li></ul></li>…</ul>
const refComponents = {
  a: CustomLinkRenderer,
  ul: ({ node, children }) => {
    const hasTitledItems = node.children?.some(
      (c) =>
        c.tagName === 'li' && c.children?.some((g) => g.tagName === 'em'),
    );
    if (hasTitledItems) {
      return <div className="mt-2 grid gap-2">{children}</div>;
    }
    return (
      <ul className="my-1 list-disc space-y-1 pl-5 text-ink-600">{children}</ul>
    );
  },
  li: ({ node, children }) => {
    const emNode = node.children?.find((c) => c.tagName === 'em');
    if (emNode) {
      const titleText = getNodeText(emNode).trim();
      const title = renderReferenceTitle(emNode);
      const nested = node.children?.find((c) => c.tagName === 'ul');
      const excerpt = (
        nested ? getNodeText(nested) : getNodeText(node).replace(titleText, '')
      ).trim();
      return <ReferenceCard title={title} excerpt={excerpt} />;
    }
    return <li>{children}</li>;
  },
};

const RecommendedReading = ({ title, content }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { t } = useTranslation();
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  return (
    <div className="my-2">
      <div className="flex items-center gap-2">
        <strong className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-teal-700">
          {title}
        </strong>
        <button
          className="items-center text-[10.5px] md:text-xs btn expand-icon px-2 py-0.5 flex flex-wrap justify-center gap-1 font-medium"
          onClick={toggleCollapse}
          id="more"
        >
          {isCollapsed ? (
            <>
              <div className="my-auto">{t('Expand')}</div>
              <div className="my-auto">
                <PlusIcon
                  className="w-3 h-3 md:h-4 md:w-4"
                  aria-hidden="true"
                />
              </div>
            </>
          ) : (
            <>
              <div>{t('Collapse')}</div>
              <div className="my-auto">
                <MinusIcon
                  className="w-3 h-3 md:h-4 md:w-4"
                  aria-hidden="true"
                />
              </div>
            </>
          )}
        </button>
      </div>
      {!isCollapsed && (
        <ReactMarkdown
          className="msg-body flex-1 text-[12px] leading-[1.5] text-ink-800 sm:text-[12.5px]"
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeMathjax]}
          skipHtml={false}
          components={refComponents}
        >
          {content.replace(/\$/g, '&#36;')}
        </ReactMarkdown>
      )}
    </div>
  );
};

export default RecommendedReading;
